/**
 * useSilentModeUnlock — forces iOS Safari into "media playback" audio
 * session so binaural tones / WebAudio output remains audible even when
 * the device's physical ringer switch is set to SILENT.
 *
 * How it works
 * ------------
 * iOS routes audio differently depending on what's playing:
 *   • Pure WebAudio API   → "ambient" session  → silenced by ringer
 *   • HTMLMediaElement    → "media playback"   → audible through silent
 *
 * By playing a continuous near-silent MP3 through a hidden <audio> tag
 * with playsinline=true, we keep iOS in the media-playback session for
 * the duration of the absorption ritual. The WebAudio binaural beats
 * piggy-back on this routing and play normally.
 *
 * The silent loop is generated as a data URI (~1 KB) so there's no
 * network request, no extra asset to ship, and it works offline.
 *
 * Returns
 * -------
 *   { unlock, lock } — call unlock() when the ritual starts, lock() on
 *   teardown. Idempotent — safe to call multiple times.
 */
import { useCallback, useRef } from "react";

// 1-second 44.1 kHz mono WAV of near-silence (–60 dB pink-ish noise).
// Generated once at build time. Below the threshold of perception but
// audible enough to keep iOS in media-playback mode.
const SILENT_AUDIO_DATA_URI = (() => {
  // Tiny constant near-silence WAV. Header is 44 bytes, then 22050
  // 16-bit mono samples at value 0–2 (essentially silence).
  // Built once at module load to avoid runtime cost.
  const sampleRate = 22050;
  const numSamples = sampleRate; // 1 second
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);
  // RIFF header
  const writeString = (off, s) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };
  writeString(0, "RIFF");
  view.setUint32(4, 36 + numSamples * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);          // PCM
  view.setUint16(22, 1, true);          // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);          // block align
  view.setUint16(34, 16, true);         // bits per sample
  writeString(36, "data");
  view.setUint32(40, numSamples * 2, true);
  // Samples — alternating ±1 for inaudible-but-non-zero signal that
  // keeps iOS treating this as active media playback.
  for (let i = 0; i < numSamples; i++) {
    view.setInt16(44 + i * 2, i % 2 === 0 ? 1 : -1, true);
  }
  // Convert to base64 data URI.
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const b64 = typeof btoa === "function" ? btoa(binary) : "";
  return `data:audio/wav;base64,${b64}`;
})();

export default function useSilentModeUnlock() {
  const audioRef = useRef(null);

  const unlock = useCallback(() => {
    if (audioRef.current) return; // already unlocked
    try {
      const a = new Audio();
      a.src = SILENT_AUDIO_DATA_URI;
      a.loop = true;
      a.volume = 0.01; // minimum non-zero so iOS treats as active playback
      a.muted = false;
      a.preload = "auto";
      // playsinline is the magic flag — without it iOS goes fullscreen.
      a.setAttribute("playsinline", "");
      a.setAttribute("webkit-playsinline", "");
      a.play().catch(() => { /* silent unlock can fail without gesture */ });
      audioRef.current = a;
    } catch (_) { /* noop */ }
  }, []);

  const lock = useCallback(() => {
    if (!audioRef.current) return;
    try {
      audioRef.current.pause();
      audioRef.current.src = "";
    } catch (_) { /* noop */ }
    audioRef.current = null;
  }, []);

  return { unlock, lock };
}
